import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { WifiOff, AlertTriangle, RefreshCw } from "lucide-react";

import { useOptionalToast } from "../../components/Toast/toastContext";
import { NetworkError } from "../../lib/errors";
import styles from "./NetworkBanner.module.css";

// Exponential backoff schedule (mockup L389: 1s → 2s → 5s → 10s → 30s cap).
const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

type Mode = "online" | "offline" | "degraded";

/**
 * Is this failure the API not answering, as opposed to the API answering "no"?
 *
 * Only a transport failure counts, and that is what the banner says out loud:
 * "Can't reach Social Hub API". An HTTP error — 404, 409, even 500 — is the
 * server reached and heard from, and the screen that made the request says so
 * itself, in words about that request; a panel-wide "you have no connection"
 * on top of it is simply false. An error of any other type is a bug in our own
 * code, which is not the operator's connection either.
 *
 * This is narrower than pingApi's rule below on purpose: that one answers a
 * different question — "is it safe to say we are back?" — where an API that
 * answers 500 to everything is no proof the outage is over.
 */
function apiUnreachable(error: unknown): boolean {
  return error instanceof NetworkError;
}

/**
 * Probes a tiny endpoint to decide whether the API is reachable. We use
 * `/api/admin/auth/me` because it is cheap and always defined; a 2xx **or**
 * 401/403 both count as "API reachable" (the network round-trip succeeded).
 * Only network failures / 5xx are treated as "still degraded".
 */
async function pingApi(signal: AbortSignal): Promise<boolean> {
  try {
    const res = await fetch("/api/admin/auth/me", {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      signal,
    });
    return res.status < 500;
  } catch {
    return false;
  }
}

/**
 * The retry loop's state: `null` while the API answers, an object once it stops
 * answering. Everything the loop needs lives here rather than in refs, so the
 * effect below owns one run of the loop from start to teardown. `attempt` only
 * exists to make "Retry now" a *new* object, which restarts the effect;
 * `immediate` tells the restarted loop to probe at once instead of waiting out
 * a backoff step first.
 */
type Retry = { attempt: number; immediate: boolean };

export function NetworkBanner() {
  const queryClient = useQueryClient();
  const toast = useOptionalToast();

  const [offline, setOffline] = useState<boolean>(
    typeof navigator !== "undefined" ? !navigator.onLine : false,
  );
  const [retry, setRetry] = useState<Retry | null>(null);
  const [retrying, setRetrying] = useState<boolean>(false);

  const degraded = retry !== null;

  const recover = useCallback(() => {
    setRetry(null);
    toast?.push({
      variant: "success",
      message: "Reconnected. Data refreshed.",
    });
    void queryClient.invalidateQueries();
  }, [toast, queryClient]);

  // ── Auto-retry loop for the degraded state ────────────────────────────────
  // One effect run = one loop: it holds the backoff step, the pending timer and
  // the in-flight probe as locals, and its cleanup cancels both.
  useEffect(() => {
    if (!retry || offline) return;

    const ctrl = new AbortController();
    let step = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    function nextDelay() {
      const delay = BACKOFF_MS[Math.min(step, BACKOFF_MS.length - 1)];
      step += 1;
      return delay;
    }

    function schedule(delay: number) {
      timer = setTimeout(() => void probe(), delay);
    }

    async function probe() {
      setRetrying(true);
      const ok = await pingApi(ctrl.signal);
      setRetrying(false);
      if (ctrl.signal.aborted) return;
      if (ok) recover();
      else schedule(nextDelay());
    }

    // A manual retry probes on the next tick — a zero timer rather than a
    // synchronous call, because the first thing a probe does is set state.
    schedule(retry.immediate ? 0 : nextDelay());

    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [retry, offline, recover]);

  // ── Browser online / offline events ──────────────────────────────────────
  useEffect(() => {
    function goOnline() {
      setOffline(false);
    }
    function goOffline() {
      setOffline(true);
    }
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // ── TanStack Query: detect API failures ──────────────────────────────────
  // Both caches, because both halves of the panel talk to the same API — and
  // the mutation half is the one that needs saying. A GET that dies leaves a
  // page visibly empty; a POST that dies leaves the form exactly as it was,
  // which reads as "the button didn't work", and the operator's answer to that
  // is to press it again.
  //
  // Re-binds when `degraded` flips, which happens twice per outage — cheap
  // enough to pay for reading the flag straight instead of through a ref.
  useEffect(() => {
    // Keep the running loop's object if one is already going, so a second
    // failure doesn't restart the backoff.
    const raise = () =>
      setRetry((current) => current ?? { attempt: 0, immediate: false });

    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== "updated") return;
      const state = event.query.state;
      // Two failures, not one: queries retry once (see queryClient.ts), so a
      // single one is only half of what the client itself already tried.
      if (
        state.status === "error" &&
        state.fetchFailureCount >= 2 &&
        apiUnreachable(state.error)
      ) {
        raise();
      } else if (state.status === "success" && degraded) {
        // Any successful query → API is reachable again.
        recover();
      }
    });

    const unsubscribeMutations = queryClient
      .getMutationCache()
      .subscribe((event) => {
        if (event.type !== "updated") return;
        const state = event.mutation.state;
        // No failure count to wait for on this side: mutations don't retry
        // (queryClient.ts), and they must not — repeating a destructive action
        // is the operator's decision. The one failure is all the evidence
        // there is ever going to be.
        if (state.status === "error" && apiUnreachable(state.error)) {
          raise();
        } else if (state.status === "success" && degraded) {
          // A mutation that went through is proof the API answers, and a
          // stronger one than a query: it was written to.
          recover();
        }
      });

    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient, degraded, recover]);

  // ── Manual retry now ─────────────────────────────────────────────────────
  function onRetryNow() {
    if (offline) {
      // Can't probe the API while the browser itself is offline; force a
      // refetch in case the browser flag is stale.
      void queryClient.refetchQueries();
      return;
    }
    setRetry((current) =>
      current ? { attempt: current.attempt + 1, immediate: true } : current,
    );
  }

  // ── Decide which banner to show ──────────────────────────────────────────
  const mode: Mode = offline ? "offline" : degraded ? "degraded" : "online";
  if (mode === "online") return null;

  const isOffline = mode === "offline";
  return (
    <div
      role="status"
      aria-live="polite"
      className={`${styles.banner} ${
        isOffline ? styles.offline : styles.degraded
      }`}
    >
      <span className={styles.icon} aria-hidden="true">
        {isOffline ? <WifiOff size={14} /> : <AlertTriangle size={14} />}
      </span>
      {isOffline ? (
        <>
          <span className={styles.title}>You&apos;re offline.</span>
          <span className={styles.meta}>
            Recent changes may not save until your connection returns.
          </span>
        </>
      ) : (
        <>
          <span className={styles.title}>Can&apos;t reach Social Hub API.</span>
          <span className={styles.meta}>
            Some actions are disabled. Showing last known data.
          </span>
        </>
      )}
      <span className={styles.spacer} />
      <button
        type="button"
        className={styles.retry}
        onClick={onRetryNow}
        disabled={retrying}
      >
        <RefreshCw
          size={12}
          aria-hidden="true"
          style={retrying ? { animation: "spin 0.8s linear infinite" } : undefined}
        />
        {retrying ? "Retrying…" : "Retry now"}
      </button>
    </div>
  );
}
